import { z } from 'zod';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ComponentPayloadValidator, ComponentValidator } from '../Component';
import { environmentalWithCoolingMethod } from '../component-sections/environmental';
import { TripCurves } from '../fields';
import { AbstractPort, Port } from '../fields/AbstractPort';
import { BreakerType } from '../fields/BreakerType';
import { Current } from '../fields/Current';
import { Curve } from '../fields/Curve';
import { Inductance } from '../fields/Inductance';
import { IsolationVoltage } from '../fields/IsolationVoltage';
import { OpeningTime } from '../fields/OpeningTime';
import { Poles } from '../fields/Poles';
import { ProtectionDirection } from '../fields/ProtectionDirection';
import { Time } from '../fields/Time';
import { Voltage } from '../fields/Voltage';
import { synchronize, synchronizePowerFlowDirection } from '../helpers';

const extraSpecifications = {
    voltagePerPole: Voltage.validators.value,
    instantaneousShortCircuitCurrentUL: Current.validators.value,
    serviceShortCircuitBreakingCapacityIEC: Current.validators.value,
    ultimateShortCircuitBreakingCapacityIEC: Current.validators.value,
    criticalClearingTime: Time.validators.value,
    poles: Poles.validator.default('2P'),
};

const port = AbstractPort.extend({
    AC: Port.AC.omit({
        power: true,
    })
        .extend({
            current: Current.validators.nom,
            ...extraSpecifications,
        })
        .default({}),
    DC: Port.DC.omit({
        power: true,
    })
        .extend({
            current: Current.validators.nom,
            ...extraSpecifications,
        })
        .default({}),
    tripCurve: TripCurves.validator.default(['C']),
    customTripCurve: Curve.validator.optional(),
    inductance: Inductance.validators.minMax,
    inrushCurrent: Current.validators.value,
    overvoltageCategory: z.number().optional(),
    residualCurrentDeviceTripLevel: Current.validators.value,
}).default({
    powerFlowDirection: 'bidirectional',
});

const electrical = z
    .object({
        ports: z.tuple([port, port]).default([undefined, undefined]),
        standards: z.array(z.string()).default([]),
        isolationVoltage: IsolationVoltage.validator,
        polaritySensitive: z.boolean().default(false),
        openingTime: OpeningTime.validator,
        protectionDirection: ProtectionDirection.validator,
        breakerType: BreakerType.validator.nullable().default(null),
        residualCurrentDevice: z
            .object({
                enabled: z.boolean().default(false),
                ratedResidualOperatingCurrent: Current.validators.value,
            })
            .default({}),
        auxiliaryContact: z
            .object({
                enabled: z.boolean().default(false),
            })
            .default({}),
    })
    .default({});

const CircuitBreakerBase = z.object({
    type: z.literal('breaker').default('breaker'),
    canServeAs: z.array(ComponentValidator.shape.type).default([]),
    electrical: synchronize(synchronizePowerFlowDirection(electrical), {
        'ports.0': ['ports.1'],
    }),
    environmental: environmentalWithCoolingMethod,
});

export const CircuitBreakerValidator = ComponentValidator.merge(CircuitBreakerBase);

export const CircuitBreakerPayloadValidator = ComponentPayloadValidator.merge(CircuitBreakerBase);

export const CircuitBreakerDefinition: ComponentDefinition = {
    type: 'breaker',
    name: 'Circuit Breaker',
    plural: 'Circuit Breakers',
    indicator: 'Q',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    actsAsSwitchgear: true,
    ports: {
        min: 2,
        max: 2,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
        displayAsSinglePort: true,
    },
    canServeAsOptions: ['contactor', 'disconnect'],
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: CircuitBreakerValidator,
        payload: CircuitBreakerPayloadValidator,
    },
};

export type CircuitBreaker = z.infer<typeof CircuitBreakerValidator>;
export type CircuitBreakerPayload = z.infer<typeof CircuitBreakerPayloadValidator>;
